import { formatGiftBalance } from '../../../../components/horasRegalo/giftBalance';
import ClientGiftQr from '../../../../components/horasRegalo/ClientGiftQr';
import HorasRegaloSesion from '../../../../components/HorasRegaloSesion';
// SesionesCitaPage.tsx — Rediseño con sistema Co Marca (igual que AboutusProjects / MiDashboard)
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { fetchSesionesByCita, Sesion } from '../../../../services/sesionesService';
import { fetchCitaById, Cita } from '../../../../services/citasService';
import {
    Clock, Calendar, ArrowLeft, Info, Gift,
    CreditCard, DollarSign, CheckCircle, AlertCircle,
    MoreVertical, History, Sun, Briefcase, ClipboardCheck,
} from 'lucide-react';
import AsistenciaSesionModal from '../../../shared/AsistenciaSesionModal';
import { getSessionAttendanceDate } from './attendanceDate';
import PagoSesionModal from '../../../../pages/Admin/pagos/PagoSesionModal';
import HistorialPagosModal from '../../../../pages/Admin/pagos/HistorialPagosSesionesModal';
import { useTheme } from '../../../../context/ThemeContext';
import {
    fetchUsoHorasRegaloPorSesion,
    UsoHorasRegalo
} from '../../../../services/usarHorasDeRegaloService';

// ── Design tokens Co Marca ──
const colors = {
    primary: '#61257B',
    primaryDark: '#451759',
    secondary: '#C1348B',
    secondaryLight: '#FFD97C',
    accent: '#FFD97C',
    accentDark: '#EA8406',
    moradoPrincipal: '#61257B',
    moradoVariante: '#C1348B',
};

// ── Section wrapper CSS animation ──
function Section({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
    return (
        <div style={{ animation: `sesionFadeUp 0.6s cubic-bezier(.16,1,.3,1) ${delay}s both` }}>
            {children}
        </div>
    );
}

// ── SectionTitle (igual que MiDashboard) ──
function SectionTitle({ label, title, accent, sub }: { label: string; title: string; accent: string; sub?: string }) {
    const { isDark } = useTheme();
    return (
        <div style={{ marginBottom: '20px' }}>
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '7px',
                background: `${accent}12`, border: `1.5px solid ${accent}25`,
                borderRadius: '100px', padding: '5px 14px',
                fontSize: '0.7rem', fontWeight: '800', color: accent,
                letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px',
            }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: accent, display: 'inline-block' }} />
                {label}
            </span>
            <h2 style={{
                fontSize: 'clamp(1.1rem, 2.5vw, 1.55rem)', fontWeight: '900',
                color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.1,
            }}>
                {title}
            </h2>
            {sub && <p style={{ fontSize: '0.85rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500', marginTop: '5px' }}>{sub}</p>}
        </div>
    );
}

// ── Badge helpers ──
const estadoBadge = (estado: string) => {
    const map: Record<string, { label: string; color: string; Icon: any }> = {
        completo:  { label: 'Pagado completo', color: colors.secondaryLight, Icon: CheckCircle },
        parcial:   { label: 'Pagado parcial',  color: colors.accentDark,     Icon: AlertCircle },
        pendiente: { label: 'Pendiente',        color: colors.primary,        Icon: AlertCircle },
    };
    const cfg = map[estado?.toLowerCase()] ?? { label: estado, color: '#94a3b8', Icon: AlertCircle };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            background: `${cfg.color}12`, border: `1.5px solid ${cfg.color}28`,
            borderRadius: '100px', padding: '4px 12px',
            fontSize: '0.7rem', fontWeight: '700', color: cfg.color,
        }}>
            <cfg.Icon size={11} strokeWidth={2.5} />
            {cfg.label.toUpperCase()}
        </span>
    );
};

const finSemanaBadge = (finSemana: string) => {
    const esFinSemana = finSemana === 'Sí' || finSemana === 'si';
    const color = esFinSemana ? colors.secondaryLight : '#64748b';
    const Icon = esFinSemana ? Sun : Briefcase;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            background: `${color}10`, border: `1.5px solid ${color}22`,
            borderRadius: '100px', padding: '3px 10px',
            fontSize: '0.68rem', fontWeight: '700', color,
        }}>
            <Icon size={10} strokeWidth={2.5} />
            {esFinSemana ? 'Con fin de semana' : 'Solo días hábiles'}
        </span>
    );
};

// ── Sesion Card (patrón ProjectCard) ──
function SesionCard({
    sesion, index, onVerHistorial, onVerHorasRegalo, onAsistencia, asistenciaDisponible, onGiftChanged,
}: {
    sesion: Sesion;
    index: number;
    onVerHistorial: () => void;
    onVerHorasRegalo: () => void;
    onAsistencia: () => void;
    asistenciaDisponible: boolean;
    onGiftChanged: () => void;
}) {
    const { isDark } = useTheme();
    const [hovered, setHovered] = useState(false);
    const [dropOpen, setDropOpen] = useState(false);
    const dropRef = useRef<HTMLDivElement>(null);
    const hasFinancialData = (
        sesion.estado_pago !== undefined
        && sesion.monto_pagado !== undefined
        && sesion.saldo_pendiente !== undefined
        && sesion.precio_con_descuento !== undefined
    );

    const accentColor = sesion.estado_pago === 'completo'
        ? colors.secondaryLight
        : sesion.estado_pago === 'parcial'
        ? colors.accentDark
        : colors.primary;

    const formatCurrency = (n: number) =>
        new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB' }).format(n);

    const formatDate = (d: string) => {
        if (!d) return '—';
        const [y, m, day] = d.split('-').map(Number);
        return new Date(y, m - 1, day).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (dropRef.current && !dropRef.current.contains(e.target as Node)) setDropOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                background: isDark ? 'rgba(22, 10, 43, 0.88)' : '#fff',
                borderRadius: '20px',
                border: hovered 
                    ? `1.5px solid ${accentColor}60` 
                    : (isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #f1f5f9'),
                boxShadow: hovered
                    ? (isDark ? `0 16px 48px rgba(0,0,0,0.6), 0 0 20px ${accentColor}25` : `0 16px 48px ${accentColor}14, 0 4px 20px rgba(0,0,0,0.06)`)
                    : (isDark ? '0 4px 20px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.04)'),
                transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
                transition: 'transform 0.3s cubic-bezier(.16,1,.3,1), box-shadow 0.3s, border-color 0.3s',
                animation: `sesionFadeUp 0.5s cubic-bezier(.16,1,.3,1) ${Math.min(index, 7) * 0.06}s both`,
                backdropFilter: 'blur(12px)',
                display: 'flex', flexDirection: 'column',
            }}
        >
            {/* Barra acento top */}
            <div style={{
                height: '4px',
                background: hovered
                    ? `linear-gradient(90deg, ${accentColor}, ${accentColor}50)`
                    : `linear-gradient(90deg, ${accentColor}50, ${accentColor}14)`,
                borderRadius: '20px 20px 0 0',
                transition: 'background 0.3s',
            }} />

            {/* Header card */}
            <div style={{ padding: '20px 20px 14px', borderBottom: isDark ? '1px solid rgba(193, 52, 139, 0.15)' : `1px solid ${accentColor}10` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {hasFinancialData && estadoBadge(sesion.estado_pago!)}
                        <p style={{
                            fontSize: '1.15rem', fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a',
                            letterSpacing: '-0.02em', marginTop: '8px', lineHeight: 1.1,
                        }}>
                            Sesión <span style={{ color: accentColor }}>#{sesion.id}</span>
                        </p>
                    </div>

                    {/* Dropdown financiero */}
                    {hasFinancialData && <div style={{ position: 'relative', flexShrink: 0 }} ref={dropRef}>
                        <button
                            onClick={() => setDropOpen(!dropOpen)}
                            style={{
                                width: '32px', height: '32px', borderRadius: '10px',
                                background: dropOpen ? `${accentColor}12` : (isDark ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc'),
                                border: isDark ? '1.5px solid rgba(255, 255, 255, 0.1)' : `1.5px solid ${dropOpen ? accentColor + '30' : '#f1f5f9'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', transition: 'all 0.2s',
                            }}
                        >
                            <MoreVertical size={14} color={dropOpen ? accentColor : (isDark ? '#cbd5e1' : '#64748b')} />
                        </button>
                        {dropOpen && (
                            <div style={{
                                position: 'absolute', right: 0, top: 'calc(100% + 6px)',
                                width: '180px', background: isDark ? '#190a30' : '#fff',
                                borderRadius: '14px', border: isDark ? '1.5px solid rgba(193, 52, 139, 0.35)' : '1.5px solid #f1f5f9',
                                boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.6)' : '0 12px 32px rgba(0,0,0,0.10)',
                                overflow: 'hidden', zIndex: 20,
                                fontFamily: "'DM Sans', sans-serif",
                            }}>
                                <div style={{ height: '3px', background: `linear-gradient(90deg, ${accentColor}, ${accentColor}40)` }} />
                                <div style={{ padding: '6px' }}>
                                    {[
                                        { icon: History, label: 'Ver Historial', action: () => { onVerHistorial(); setDropOpen(false); }, color: colors.secondary },
                                        { icon: Gift, label: 'Horas Regalo', action: () => { onVerHorasRegalo(); setDropOpen(false); }, color: colors.accentDark },
                                    ].map(({ icon: Icon, label, action, color }) => (
                                        <button
                                            key={label}
                                            onClick={action}
                                            style={{
                                                width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                                                padding: '9px 10px', borderRadius: '9px',
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#ffffff' : '#374151',
                                                fontFamily: "'DM Sans', sans-serif", textAlign: 'left',
                                                transition: 'background 0.15s',
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : `${color}08`; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                                        >
                                            <div style={{
                                                width: '26px', height: '26px', borderRadius: '8px',
                                                background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <Icon size={13} color={color} />
                                            </div>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>}
                </div>

                {/* Fin semana badge */}
                {finSemanaBadge(sesion.fin_semana)}
            </div>

            {/* Body */}
            <div style={{ padding: '16px 20px', flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>

                {/* Fechas */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <div style={{
                        width: '32px', height: '32px', borderRadius: '10px', flexShrink: 0,
                        background: isDark ? 'rgba(97, 37, 123, 0.15)' : `${colors.secondary}10`, border: `1.5px solid ${colors.secondary}18`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.3s',
                        ...(hovered ? { background: colors.secondary, border: `1.5px solid ${colors.secondary}` } : {}),
                    }}>
                        <Calendar size={14} color={hovered ? '#fff' : colors.secondary} />
                    </div>
                    <div>
                        <p style={{ fontSize: '0.68rem', fontWeight: '700', color: isDark ? '#94a3b8' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Período</p>
                        <p style={{ fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#ffffff' : '#374151', lineHeight: 1.4 }}>
                            {formatDate(sesion.fecha_inicio)}
                        </p>
                        <p style={{ fontSize: '0.78rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500' }}>
                            al {formatDate(sesion.fecha_fin)}
                        </p>
                    </div>
                </div>

                {/* Horario */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <div style={{
                        width: '32px', height: '32px', borderRadius: '10px', flexShrink: 0,
                        background: isDark ? 'rgba(234, 132, 6, 0.15)' : `${colors.accentDark}10`, border: `1.5px solid ${colors.accentDark}18`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.3s',
                        ...(hovered ? { background: colors.accentDark, border: `1.5px solid ${colors.accentDark}` } : {}),
                    }}>
                        <Clock size={14} color={hovered ? '#fff' : colors.accentDark} />
                    </div>
                    <div>
                        <p style={{ fontSize: '0.68rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Horario</p>
                        <p style={{ fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#ffffff' : '#374151' }}>
                            {sesion.hora_inicio} – {sesion.hora_fin || 'En curso'}
                        </p>
                        <p style={{ fontSize: '0.78rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500' }}>
                            {sesion.horas} {sesion.horas === 1 ? 'hora' : 'horas'}
                        </p>
                    </div>
                </div>

                {hasFinancialData && <>
                <HorasRegaloSesion sesion={sesion} />
                {sesion.tiene_horas_regalo && <ClientGiftQr sesion={sesion} onChanged={onGiftChanged} />}
                {/* Precio */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: isDark ? 'rgba(139, 63, 168, 0.08)' : `${colors.secondaryLight}06`,
                    border: isDark ? '1.5px solid rgba(139, 63, 168, 0.25)' : `1.5px solid ${colors.secondaryLight}14`,
                    borderRadius: '12px', padding: '12px 14px', marginTop: '4px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                            width: '28px', height: '28px', borderRadius: '8px',
                            background: `${colors.secondaryLight}14`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <DollarSign size={13} color={colors.secondaryLight} />
                        </div>
                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#64748b' }}>Precio sesión</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: '1rem', fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.02em', lineHeight: 1 }}>
                            {formatCurrency(sesion.precio_con_descuento)}
                        </p>
                        {sesion.precio_con_descuento !== sesion.precio && (
                            <p style={{ fontSize: '0.68rem', color: isDark ? '#94a3b8' : '#94a3b8', textDecoration: 'line-through', marginTop: '2px' }}>
                                {formatCurrency(sesion.precio)}
                            </p>
                        )}
                    </div>
                </div>

                {/* Estado pago - divisor + montos */}
                <div style={{
                    paddingTop: '12px', borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.06)' : `1px solid ${accentColor}12`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <CreditCard size={13} color="#94a3b8" />
                        <span style={{ fontSize: '0.72rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight: '600' }}>Estado de pago</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: '0.82rem', fontWeight: '700', color: colors.secondaryLight }}>
                            Pagado: {formatCurrency(sesion.monto_pagado!)}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: sesion.saldo_pendiente! > 0 ? colors.primary : '#94a3b8', fontWeight: '600' }}>
                            Saldo: {formatCurrency(sesion.saldo_pendiente!)}
                        </p>
                    </div>
                </div>
                </>}
            </div>

            {/* Footer acciones */}
            <div style={{ padding: '14px 20px', borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid #f1f5f9', display: 'flex', gap: '8px' }}>
                {asistenciaDisponible && <button
                    onClick={onAsistencia}
                    style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        padding: '9px 12px', borderRadius: '11px',
                        background: `linear-gradient(135deg, #7a0039, ${colors.primary})`,
                        border: 'none', color: '#fff', fontSize: '0.78rem', fontWeight: '700',
                        cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                    }}
                >
                    <ClipboardCheck size={13} strokeWidth={2} />
                    Mi ingreso / salida
                </button>}
                {hasFinancialData && <>
                <button
                    onClick={onVerHorasRegalo}
                    disabled={!sesion.tiene_horas_regalo}
                    style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        padding: '9px 12px', borderRadius: '11px',
                        background: !sesion.tiene_horas_regalo
                            ? (isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc')
                            : `linear-gradient(135deg, ${colors.moradoPrincipal}, ${colors.moradoVariante})`,
                        border: !sesion.tiene_horas_regalo ? (isDark ? '1.5px solid rgba(255, 255, 255, 0.08)' : '1.5px solid #f1f5f9') : 'none',
                        color: !sesion.tiene_horas_regalo ? (isDark ? '#64748b' : '#94a3b8') : '#fff',
                        fontSize: '0.78rem', fontWeight: '700',
                        cursor: !sesion.tiene_horas_regalo ? 'not-allowed' : 'pointer',
                        fontFamily: "'DM Sans', sans-serif",
                        transition: 'all 0.2s',
                        boxShadow: sesion.tiene_horas_regalo ? '0 4px 14px rgba(193, 52, 139, 0.35)' : 'none',
                    }}
                    onMouseEnter={e => { if (sesion.tiene_horas_regalo) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                    <Gift size={13} strokeWidth={2} />
                    Horas Regalo
                </button>
                <button
                    onClick={onVerHistorial}
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        padding: '9px 14px', borderRadius: '11px',
                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                        border: 'none', color: '#fff',
                        fontSize: '0.78rem', fontWeight: '700',
                        cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                        boxShadow: '0 4px 14px rgba(97, 37, 123, 0.35)',
                        transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 6px 18px rgba(193, 52, 139, 0.5)';
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(97, 37, 123, 0.35)';
                    }}
                >
                    <History size={13} strokeWidth={2} />
                    Historial
                </button>
                </>}
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════
const SesionesCitaPage: React.FC = () => {
    const { isDark } = useTheme();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const [sesiones, setSesiones] = useState<Sesion[]>([]);
    const [cita, setCita] = useState<Cita | null>(null);
    const [loading, setLoading] = useState(true);

    const [modalPagoAbierto, setModalPagoAbierto] = useState(false);
    const [sesionSeleccionada, setSesionSeleccionada] = useState<Sesion | null>(null);
    const [modalHorasRegaloAbierto, setModalHorasRegaloAbierto] = useState(false);
    const [horasRegaloSesion, setHorasRegaloSesion] = useState<UsoHorasRegalo | null>(null);
    const [horasRegaloCliente, setHorasRegaloCliente] = useState<UsoHorasRegalo[]>([]);
    const [cargandoHorasRegalo, setCargandoHorasRegalo] = useState(false);
    const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
    const [modalAsistenciaAbierto, setModalAsistenciaAbierto] = useState(false);
    const [clienteId, setClienteId] = useState<string | null>(null);

    const searchParams = new URLSearchParams(location.search);
    const clienteIdParam = searchParams.get('clienteId');

    useEffect(() => {
        const loadData = async () => {
            try {
                const [citaData, sesionesData] = await Promise.all([
                    fetchCitaById(Number(id)),
                    fetchSesionesByCita(Number(id))
                ]);
                setCita(citaData);
                setSesiones(sesionesData);
                if (clienteIdParam) setClienteId(clienteIdParam);
                else if (citaData?.cliente) setClienteId(citaData.cliente.toString());
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [id, clienteIdParam]);

    const abrirModalHorasRegalo = async (sesion: Sesion) => {
        setSesionSeleccionada(sesion);
        setCargandoHorasRegalo(true);
        try {
            if (!sesion.tiene_horas_regalo) {
                setHorasRegaloSesion(null);
                setHorasRegaloCliente([]);
                setModalHorasRegaloAbierto(true);
                return;
            }
            const horasRegalo = await fetchUsoHorasRegaloPorSesion(sesion.id);
            setHorasRegaloSesion(horasRegalo ?? null);
            setHorasRegaloCliente([]);
        } catch (error) {
            console.error('Error cargando horas regalo:', error);
            setHorasRegaloSesion(null);
            setHorasRegaloCliente([]);
        } finally {
            setCargandoHorasRegalo(false);
            setModalHorasRegaloAbierto(true);
        }
    };

    const abrirModalHistorial = (sesion: Sesion) => {
        setSesionSeleccionada(sesion);
        setModalHistorialAbierto(true);
    };

    const recargarSesiones = async () => {
        try {
            const sesionesData = await fetchSesionesByCita(Number(id));
            setSesiones(sesionesData);
        } catch (err) {
            console.error('Error recargando sesiones:', err);
        }
    };

    const formatCurrency = (n: number) =>
        new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB' }).format(n);

    const formatDate = (d: string) => {
        if (!d) return '—';
        const [y, m, day] = d.split('-').map(Number);
        return new Date(y, m - 1, day).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    // Resumen stats de sesiones
    const totalPagado = sesiones.reduce((s, ses) => s + (Number(ses.monto_pagado) || 0), 0);
    const totalPendiente = sesiones.reduce((s, ses) => s + (Number(ses.saldo_pendiente) || 0), 0);
    const sesionesCompletas = sesiones.filter(s => s.estado_pago === 'completo').length;

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif", background: 'transparent', minHeight: '100vh' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
                * { box-sizing: border-box; }
                @keyframes blobFloat {
                    0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
                    50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
                }
                @keyframes sesionFadeUp {
                    from { opacity: 0; transform: translateY(22px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes pulseD {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(193,52,139,0.35); }
                    50%       { box-shadow: 0 0 0 5px rgba(193,52,139,0); }
                }
                .blob-ses { animation: blobFloat 9s ease-in-out infinite; }
                .ses-back-btn:hover { background: ${colors.secondary}12 !important; color: ${colors.secondary} !important; }
                .ses-back-btn:hover svg { color: ${colors.secondary} !important; }
            `}</style>

            {/* Modales */}
            <PagoSesionModal
                abierto={modalPagoAbierto}
                onCerrar={() => setModalPagoAbierto(false)}
                sesion={sesionSeleccionada}
                clienteId={clienteId}
                onPagoExitoso={recargarSesiones}
            />
            <HistorialPagosModal
                abierto={modalHistorialAbierto}
                onCerrar={() => setModalHistorialAbierto(false)}
                sesionId={sesionSeleccionada?.id || 0}
            />


            {/* Modal Horas Regalo — rediseñado */}
            {modalHorasRegaloAbierto && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 50,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '16px',
                }}>
                    <div style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(15,23,42,0.45)',
                        backdropFilter: 'blur(4px)',
                    }} onClick={() => setModalHorasRegaloAbierto(false)} />

                    <div style={{
                        background: isDark ? 'rgba(22, 10, 43, 0.96)' : '#fff',
                        borderRadius: '24px', width: '100%', maxWidth: '520px',
                        boxShadow: isDark ? '0 32px 80px rgba(0,0,0,0.6)' : '0 32px 80px rgba(0,0,0,0.18)',
                        border: isDark ? '1.5px solid rgba(193, 52, 139, 0.35)' : 'none',
                        overflow: 'hidden', position: 'relative', zIndex: 1,
                        animation: 'sesionFadeUp 0.35s cubic-bezier(.16,1,.3,1) both',
                        fontFamily: "'DM Sans', sans-serif",
                    }}>
                        {/* Header degradado */}
                        <div style={{
                            background: `linear-gradient(135deg, ${colors.primaryDark}, ${colors.primary})`,
                            padding: '24px 24px 20px',
                            position: 'relative', overflow: 'hidden',
                        }}>
                            <div className="blob-ses" style={{
                                position: 'absolute', top: '-60px', right: '-60px',
                                width: '200px', height: '200px', pointerEvents: 'none',
                                background: 'rgba(255,255,255,0.06)',
                            }} />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        width: '40px', height: '40px', borderRadius: '12px',
                                        background: 'rgba(255,255,255,0.15)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Gift size={20} color="#fff" />
                                    </div>
                                    <div>
                                        <h2 style={{ fontSize: '1rem', fontWeight: '800', color: '#fff', lineHeight: 1.1 }}>
                                            Horas Regalo
                                        </h2>
                                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                                            Sesión #{sesionSeleccionada?.id}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setModalHorasRegaloAbierto(false)}
                                    style={{
                                        width: '32px', height: '32px', borderRadius: '10px',
                                        background: 'rgba(255,255,255,0.12)', border: 'none',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', color: '#fff', transition: 'background 0.2s',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '24px', maxHeight: '60vh', overflowY: 'auto' }}>
                            {cargandoHorasRegalo ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0', gap: '12px' }}>
                                    <div style={{
                                        width: '40px', height: '40px', borderRadius: '50%',
                                        border: `3px solid ${colors.secondary}20`,
                                        borderTop: `3px solid ${colors.secondary}`,
                                        animation: 'spin 0.7s linear infinite',
                                    }} />
                                    <p style={{ fontSize: '0.85rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '500' }}>Cargando horas regalo...</p>
                                </div>
                            ) : horasRegaloSesion ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    {/* Info sesión */}
                                    <div style={{
                                        background: isDark ? 'rgba(139, 63, 168, 0.08)' : `${colors.secondaryLight}08`,
                                        border: isDark ? '1.5px solid rgba(139, 63, 168, 0.22)' : `${colors.secondaryLight}22`,
                                        borderRadius: '14px', padding: '14px 16px',
                                    }}>
                                        <p style={{ fontSize: '0.7rem', fontWeight: '800', color: colors.secondaryLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                                            Info de la sesión
                                        </p>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: '4px' }}>
                                            <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Fecha</span>
                                            <span style={{ fontWeight: '700', color: isDark ? '#f1f5f9' : '#0f172a' }}>{formatDate(sesionSeleccionada?.fecha_inicio || '')}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                                            <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Horario</span>
                                            <span style={{ fontWeight: '700', color: isDark ? '#f1f5f9' : '#0f172a' }}>{sesionSeleccionada?.hora_inicio} – {sesionSeleccionada?.hora_fin || 'En curso'}</span>
                                        </div>
                                    </div>

                                    {/* Horas aplicadas highlight */}
                                    <div style={{
                                        background: isDark
                                            ? 'linear-gradient(135deg, rgba(139, 63, 168, 0.15), rgba(97, 37, 123, 0.12))'
                                            : `linear-gradient(135deg, ${colors.secondaryLight}14, ${colors.secondary}08)`,
                                        border: isDark ? '1.5px solid rgba(139, 63, 168, 0.35)' : `1.5px solid ${colors.secondaryLight}30`,
                                        borderRadius: '14px', padding: '16px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    }}>
                                        <div>
                                            <p style={{ fontSize: '0.75rem', fontWeight: '700', color: colors.secondaryLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Horas Regalo Aplicadas</p>
                                            <p style={{ fontSize: '0.78rem', color: isDark ? '#cbd5e1' : '#64748b', marginTop: '2px' }}>Total asignado a esta sesión</p>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span style={{ fontSize: '2rem', fontWeight: '900', color: isDark ? '#fff' : '#0f172a', letterSpacing: '-0.03em' }}>
                                                {formatGiftBalance(Number(horasRegaloSesion.horas_regalo_aplicadas) * 60)}
                                            </span>
                                            <span style={{ fontSize: '0.82rem', color: isDark ? '#cbd5e1' : '#64748b', marginLeft: '4px' }}>h:min</span>
                                        </div>
                                    </div>

                                    {/* 3 stats */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                                        {[
                                            { label: 'Asignadas', value: Number(horasRegaloSesion.horas_regalo_aplicadas) * 60, color: isDark ? '#94a3b8' : '#64748b' },
                                            { label: 'Usadas', value: Number(horasRegaloSesion.horas_usadas || 0) * 60, color: colors.primary },
                                            { label: 'Disponibles', value: horasRegaloSesion.minutos_disponibles ?? 0, color: colors.secondary },
                                        ].map(({ label, value, color }) => (
                                            <div key={label} style={{
                                                background: isDark ? 'rgba(255, 255, 255, 0.04)' : `${color}06`,
                                                border: isDark ? '1.5px solid rgba(255, 255, 255, 0.08)' : `${color}18`,
                                                borderRadius: '12px', padding: '12px', textAlign: 'center',
                                            }}>
                                                <p style={{ fontSize: '1.3rem', fontWeight: '900', color, letterSpacing: '-0.02em' }}>{formatGiftBalance(value)}</p>
                                                <p style={{ fontSize: '0.68rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '600', marginTop: '2px' }}>{label}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Excedente */}
                                    {horasRegaloSesion.excedente_cobrado !== '0.00' && (
                                        <div style={{
                                            background: isDark ? 'rgba(234, 132, 6, 0.1)' : `${colors.accentDark}08`,
                                            border: isDark ? '1.5px solid rgba(234, 132, 6, 0.3)' : `${colors.accentDark}25`,
                                            borderRadius: '12px', padding: '12px 14px',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        }}>
                                            <div>
                                                <p style={{ fontSize: '0.78rem', fontWeight: '700', color: colors.accentDark }}>Excedente cobrado</p>
                                                <p style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#94a3b8' }}>Por horas adicionales</p>
                                            </div>
                                            <span style={{ fontSize: '1rem', fontWeight: '900', color: colors.accentDark }}>
                                                {formatCurrency(parseFloat(horasRegaloSesion.excedente_cobrado))}
                                            </span>
                                        </div>
                                    )}

                                    {/* Config */}
                                    <div style={{ paddingTop: '4px', borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #f1f5f9' }}>
                                        <p style={{ fontSize: '0.72rem', fontWeight: '700', color: isDark ? '#94a3b8' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Configuración asociada</p>
                                        <p style={{ fontSize: '0.82rem', color: isDark ? '#e2e8f0' : '#374151', fontWeight: '500', lineHeight: '1.5' }}>
                                            {horasRegaloSesion.config_horas_regalo_display}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px', gap: '14px' }}>
                                    <div style={{
                                        width: '60px', height: '60px', borderRadius: '18px',
                                        background: `${colors.accentDark}08`, border: `1.5px solid ${colors.accentDark}15`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Gift size={28} color={colors.accentDark} strokeWidth={1.5} />
                                    </div>
                                    <p style={{ fontSize: '0.95rem', fontWeight: '700', color: isDark ? '#f1f5f9' : '#374151' }}>
                                        {sesionSeleccionada?.tiene_horas_regalo ? 'Horas regalo no encontradas' : 'Sin horas regalo'}
                                    </p>
                                    <p style={{ fontSize: '0.82rem', color: isDark ? '#94a3b8' : '#94a3b8', textAlign: 'center', fontWeight: '500' }}>
                                        Esta sesión no tiene horas regalo asignadas
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Footer modal */}
                        <div style={{ padding: '14px 24px', borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setModalHorasRegaloAbierto(false)}
                                style={{
                                    padding: '10px 24px', borderRadius: '12px',
                                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                    border: 'none', color: '#fff',
                                    fontSize: '0.85rem', fontWeight: '700',
                                    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                    boxShadow: '0 4px 14px rgba(97, 37, 123, 0.35)',
                                    transition: 'all 0.2s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── PÁGINA PRINCIPAL ── */}
            <div style={{ maxWidth: '1280px', margin: '0 auto', padding: 'clamp(24px, 4vw, 40px) clamp(16px, 3vw, 28px)' }}>

                {/* ══ HERO HEADER ══ */}
                <Section>
                    <div style={{
                        position: 'relative', overflow: 'hidden',
                        borderRadius: '28px',
                        background: isDark
                            ? 'linear-gradient(135deg, rgba(22, 10, 43, 0.92) 0%, rgba(32, 12, 58, 0.85) 50%, rgba(44, 15, 78, 0.9) 100%)'
                            : 'linear-gradient(135deg, #ffffff 0%, #faf6fd 50%, #f4e8f9 100%)',
                        border: isDark ? '1.5px solid rgba(193, 52, 139, 0.28)' : '1.5px solid rgba(97, 37, 123, 0.12)',
                        padding: 'clamp(28px, 4vw, 44px) clamp(20px, 4vw, 40px)',
                        marginBottom: '28px',
                        boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 4px 32px rgba(97, 37, 123, 0.06)',
                    }}>
                        {/* Blob */}
                        <div className="blob-ses" style={{
                            position: 'absolute', top: '-80px', right: '-80px',
                            width: '340px', height: '340px', pointerEvents: 'none',
                            background: `linear-gradient(135deg, ${colors.primary}10, ${colors.secondary}08)`,
                        }} />
                        {/* Puntos */}
                        <div style={{
                            position: 'absolute', bottom: '20px', left: '3%', opacity: 0.07,
                            display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '12px',
                            pointerEvents: 'none',
                        }}>
                            {[...Array(25)].map((_, i) => (
                                <div key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: colors.primaryDark }} />
                            ))}
                        </div>

                        <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '24px' }}>
                            {/* Left */}
                            <div>
                                {/* Back button */}
                                <button
                                    className="ses-back-btn"
                                    onClick={() => navigate(`/mis-citas/${id}`)}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '7px',
                                        background: isDark ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc',
                                        border: isDark ? '1.5px solid rgba(255, 255, 255, 0.12)' : '1.5px solid #f1f5f9',
                                        borderRadius: '10px', padding: '7px 14px',
                                        fontSize: '0.8rem', fontWeight: '700',
                                        color: isDark ? '#cbd5e1' : '#64748b',
                                        cursor: 'pointer', marginBottom: '16px',
                                        transition: 'all 0.2s', fontFamily: "'DM Sans', sans-serif",
                                    }}
                                >
                                    <ArrowLeft size={14} />
                                    Volver a citas
                                </button>

                                {/* Badge */}
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '7px',
                                    background: isDark ? 'rgba(193, 52, 139, 0.2)' : `${colors.primary}12`,
                                    border: isDark ? '1.5px solid rgba(193, 52, 139, 0.4)' : `1.5px solid ${colors.primary}25`,
                                    borderRadius: '100px', padding: '5px 16px',
                                    fontSize: '0.7rem', fontWeight: '800', color: isDark ? '#ffd97c' : colors.primaryDark,
                                    letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px',
                                }}>
                                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.secondary, animation: 'pulseD 2s infinite' }} />
                                    Co Marca Group
                                </span>

                                <h1 style={{
                                    fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: '900',
                                    color: isDark ? '#fff' : '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.05, marginBottom: '8px',
                                }}>
                                    Sesiones de la <span style={{ color: isDark ? '#ffd97c' : colors.primary }}>Cita</span>
                                </h1>

                                {cita && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                                        <span style={{
                                            fontSize: '0.78rem', fontWeight: '700',
                                            color: isDark ? '#cbd5e1' : '#94a3b8',
                                            background: isDark ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc',
                                            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #f1f5f9',
                                            borderRadius: '8px', padding: '3px 10px', fontFamily: 'monospace',
                                        }}>
                                            {cita.codigo}
                                        </span>
                                    </div>
                                )}

                                {/* Barra colores marca */}
                                <div style={{ display: 'flex', gap: '5px', marginTop: '14px' }}>
                                    {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c, i) => (
                                        <div key={i} style={{ height: '3px', width: i === 0 ? '28px' : '10px', borderRadius: '3px', background: c }} />
                                    ))}
                                </div>
                            </div>

                            {/* Right: stats */}
                            {!loading && sesiones.length > 0 && (
                                <div style={{
                                    display: 'flex',
                                    background: isDark ? 'rgba(22, 10, 43, 0.88)' : '#fff',
                                    borderRadius: '18px',
                                    border: isDark ? '1.5px solid rgba(193, 52, 139, 0.28)' : '1.5px solid #f1f5f9',
                                    boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 16px rgba(0,0,0,0.04)',
                                    overflow: 'hidden', flexShrink: 0,
                                }}>
                                    {[
                                        { value: sesiones.length.toString(), label: 'Total sesiones', color: isDark ? '#ffd97c' : colors.primary },
                                        { value: sesionesCompletas.toString(), label: 'Completas', color: '#10b981' },
                                        { value: `Bs ${totalPagado.toFixed(0)}`, label: 'Total pagado', color: isDark ? '#f472b6' : colors.secondary },
                                        { value: totalPendiente > 0 ? `Bs ${totalPendiente.toFixed(0)}` : '✓ Al día', label: 'Saldo pendiente', color: totalPendiente > 0 ? '#ef4444' : '#10b981' },
                                    ].map((s, i) => (
                                        <div key={s.label} style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                                            padding: '16px 22px',
                                            borderRight: i < 3 ? (isDark ? '1.5px solid rgba(255, 255, 255, 0.08)' : '1.5px solid #f1f5f9') : 'none',
                                        }}>
                                            <div style={{ fontSize: '1.6rem', fontWeight: '900', color: s.color, lineHeight: 1, letterSpacing: '-0.03em' }}>{s.value}</div>
                                            <div style={{ fontSize: '0.68rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '600', marginTop: '4px', textAlign: 'center' }}>{s.label}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </Section>

                {/* ══ CONTENIDO ══ */}
                <Section delay={0.08}>
                    <div style={{
                        background: isDark ? 'rgba(22, 10, 43, 0.88)' : '#fff',
                        borderRadius: '24px',
                        border: isDark ? '1.5px solid rgba(193, 52, 139, 0.28)' : '1.5px solid #f1f5f9',
                        boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.04)',
                    }}>
                        {/* Barra top roja */}
                        <div style={{ height: '4px', background: `linear-gradient(90deg, ${colors.primary}, ${colors.primary}30)`, borderRadius: '24px 24px 0 0' }} />

                        <div style={{ padding: '28px' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: '28px', flexWrap: 'wrap' }}>
                                <SectionTitle
                                    label="Detalle"
                                    title="Todas las Sesiones"
                                    accent={colors.primary}
                                    sub={loading ? 'Cargando...' : `${sesiones.length} ${sesiones.length === 1 ? 'sesión registrada' : 'sesiones registradas'}`}
                                />
                                {!loading && sesiones.length > 0 && (
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        background: `${colors.secondaryLight}10`, border: `1.5px solid ${colors.secondaryLight}25`,
                                        borderRadius: '100px', padding: '6px 16px',
                                        fontSize: '0.75rem', fontWeight: '700', color: colors.secondaryLight,
                                    }}>
                                        <CheckCircle size={13} strokeWidth={2.5} />
                                        {sesionesCompletas} de {sesiones.length} completas
                                    </span>
                                )}
                            </div>

                            {/* Lista */}
                            {loading ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '72px 20px', gap: '16px' }}>
                                    <div style={{
                                        width: '48px', height: '48px', borderRadius: '50%',
                                        border: `3px solid ${colors.primary}20`,
                                        borderTop: `3px solid ${colors.primary}`,
                                        animation: 'spin 0.7s linear infinite',
                                    }} />
                                    <p style={{ fontSize: '0.9rem', fontWeight: '600', color: '#94a3b8' }}>Cargando sesiones...</p>
                                </div>
                            ) : sesiones.length === 0 ? (
                                <div style={{
                                    display: 'flex', alignItems: 'flex-start', gap: '14px',
                                    background: `${colors.accentDark}08`,
                                    border: `1.5px solid ${colors.accentDark}25`,
                                    borderLeft: `4px solid ${colors.accentDark}`,
                                    borderRadius: '14px', padding: '20px',
                                }}>
                                    <div style={{
                                        width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                                        background: `${colors.accentDark}14`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Info size={18} color={colors.accentDark} strokeWidth={2} />
                                    </div>
                                    <div>
                                        <p style={{ fontSize: '0.95rem', fontWeight: '700', color: colors.accentDark, marginBottom: '4px' }}>
                                            No hay sesiones registradas
                                        </p>
                                        <p style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>
                                            Esta cita aún no tiene sesiones asociadas.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                    gap: '16px',
                                }}>
                                    {sesiones.map((sesion, idx) => (
                                        <SesionCard
                                            key={sesion.id}
                                            sesion={sesion}
                                            index={idx}
                                            onVerHistorial={() => abrirModalHistorial(sesion)}
                                            onVerHorasRegalo={() => abrirModalHorasRegalo(sesion)}
                                            onGiftChanged={recargarSesiones}
                                            asistenciaDisponible={cita?.espacio_servicio != null}
                                            onAsistencia={() => {
                                                setSesionSeleccionada(sesion);
                                                setModalAsistenciaAbierto(true);
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </Section>
            </div>
            {modalAsistenciaAbierto && sesionSeleccionada && (
                <AsistenciaSesionModal
                    sesionId={sesionSeleccionada.id}
                    fechaAsistencia={getSessionAttendanceDate(sesionSeleccionada)}
                    modoPersonal={false}
                    onClose={() => setModalAsistenciaAbierto(false)}
                />
            )}
        </div>
    );
};

export default SesionesCitaPage;
